// Един ред в таблицата „Артикули" на модала за издаване на фактура.
// Изнесен от `InvoiceCreateModal` при добавянето на бакшиша (Tailwind).
//
// Редовете са read-only — редакция става само през бутона „Промяна".
// Click на ред = селекция (за бутоните „Промяна"/„Изтриване").
//
// Всички суми идват от споделената ДДС разбивка (lib/vat.ts), за да сумират
// ТОЧНО на тоталите. Ед. цена е нетна, закръглена независимо (информативна);
// Сума (нето) + Сума на ДДС = Сума с ДДС за всеки ред.
//
// Редът за депозит („Приспаднато авансово плащане") е заключен и с минус.
// Редът за бакшиш е заключен. Без ДДС (група А): в колоната „ДДС %" стои „—"
// (не „0%" — доброволният бакшиш не е доставка с нулева ставка), а под името —
// бележката „ДДС не се начислява — доброволен бакшиш.". Облагаемият бакшиш
// (група Б/В) изглежда като продуктов ред — със ставката си, без бележка.

import { useTranslation } from "react-i18next";

import { netUnitPrice, type VatLineBreakdown } from "../../../../../lib/vat";
import { isLockedLine, isNoVatTipLine, type Line } from "./invoiceLines";

/**
 * Общата решетка на заглавието и редовете — 9 колони (като референтния POS):
 * #, Наименование, Ед., ДДС %, Количество, Ед. цена (нето), Сума на ДДС,
 * Сума (нето), Сума с ДДС (бруто). `min-w` → хоризонтален скрол на тесни екрани.
 */
export const INVOICE_LINE_GRID_CLASS =
  "grid grid-cols-[32px_minmax(120px,1fr)_44px_56px_90px_82px_92px_82px_92px] min-w-[720px] gap-2";

const NUMBER_CELL = "text-right font-pos-mono tabular-nums";

type Props = {
  line: Line;
  index: number;
  amounts: VatLineBreakdown;
  isLast: boolean;
  isSelected: boolean;
  onSelect: () => void;
};

export const InvoiceLineRow = ({
  line,
  index,
  amounts,
  isLast,
  isSelected,
  onSelect,
}: Props) => {
  const { t } = useTranslation();
  const isNoVatTip = isNoVatTipLine(line);
  const unitPriceNet = isNoVatTip ? line.unitPrice : netUnitPrice(line.unitPrice, line.vatRate);

  return (
    <div
      role="button"
      onClick={onSelect}
      data-line-kind={isLockedLine(line) ? line.kind : undefined}
      className={`${INVOICE_LINE_GRID_CLASS} cursor-pointer items-center px-3 text-[12.5px] text-pos-ink transition-[background] duration-[120ms] ${
        isNoVatTip ? "min-h-10 py-1.5" : "h-10"
      } ${isLast ? "" : "border-b border-pos-line2"} ${
        isSelected ? "bg-pos-selected-bg" : "bg-pos-card"
      }`}
    >
      <span className="font-pos-mono text-pos-muted">{index + 1}</span>
      {isNoVatTip ? (
        <span className="flex min-w-0 flex-col">
          <span className="truncate font-semibold">{line.name}</span>
          <span className="text-[10.5px] font-medium leading-[1.3] text-pos-muted">
            {t("invoiceTips.vatNote")}
          </span>
        </span>
      ) : (
        <span className="truncate font-semibold">{line.name}</span>
      )}
      <span className="text-pos-soft">{line.unit}</span>
      <span className="font-pos-mono text-pos-soft">
        {isNoVatTip ? "—" : `${line.vatRate}%`}
      </span>
      <span className={NUMBER_CELL}>{line.quantity}</span>
      <span className={`${NUMBER_CELL} text-pos-soft`}>{unitPriceNet.toFixed(2)}</span>
      <span className={`${NUMBER_CELL} text-pos-soft`}>{amounts.vat.toFixed(2)}</span>
      <span className={`${NUMBER_CELL} font-semibold`}>{amounts.net.toFixed(2)}</span>
      <span className={`${NUMBER_CELL} font-semibold`}>{amounts.gross.toFixed(2)}</span>
    </div>
  );
};
